import { useState } from "react";
import {
  Box,
  Button,
  Pagination,
  SimpleTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  LOGIN_HISTORY_PAGE_SIZE,
  useGetLoginHistory
} from "@dealer-center/ddc-pages-configuration-fetch";
import type { MyInfoSubject } from "../../useMyInfoSubject";
import {
  BORDER_MUTED,
  ON_SURFACE_MUTED,
  SPACE_BLOCK,
  SURFACE_SUBDUED
} from "../../layoutTokens";
import { TabState } from "../TabState";
import { formatLoginDate } from "./formatLoginDate";

export interface LoginHistoryTabProps {
  subject: MyInfoSubject;
}

/**
 * A single-column, server-paginated list of sign-in date/times, most recent first (FR-037–FR-040).
 *
 * **No IP address column and no result/outcome column.** Both were dropped deliberately on 2026-08-11
 * and MUST NOT be built; the live tool's single column is labelled "Logged In".
 *
 * **The server already sorted `desc`** — the rows render in payload order and are never re-sorted here.
 *
 * **Paging is server-side.** `page` lives in component state, goes into the query key, and a change
 * refetches. `Table.Table enablePagination` is forbidden for this tab (research R-006): it paginates
 * the array it is handed, which here is a *single server page*, so it would report "1 of 1" over 25
 * rows while thousands exist. The range and position indicators are likewise derived from `totalCount`
 * and `requestedPageSize`, never from the rendered row count.
 */
export const LoginHistoryTab = ({ subject }: LoginHistoryTabProps) => {
  /*
   * One-based, because that is what the `Pagination` control and the "Page 1 of 3" indicator speak.
   * The wire is **zero-based**, so the conversion happens on the next line and nowhere else — see
   * `UseGetLoginHistoryOptions.page` for the evidence, and note it is unverified against a subject
   * with real history, since no nonprod subject has any.
   */
  const [page, setPage] = useState(1);

  const { data, isPending, isError } = useGetLoginHistory({
    userId: subject.userId,
    accountId: subject.accountId,
    page: page - 1,
    size: LOGIN_HISTORY_PAGE_SIZE
  });

  const entries = data?.loginHistory ?? [];
  /*
   * The payload is `number[]` — raw instants with no id — so the instant is the only candidate key,
   * and two sign-ins recorded in the same millisecond would collide. The occurrence counter is the
   * disambiguator: the first row for an instant keys on the instant alone, a repeat gets `#2`. It is
   * position-derived only *within* a set of identical values, so unlike an array index it does not
   * change when the page's other rows do.
   */
  const seenAt = new Map<number, number>();
  const rows = entries.map(at => {
    const occurrence = (seenAt.get(at) ?? 0) + 1;
    seenAt.set(at, occurrence);
    return { at, key: occurrence === 1 ? String(at) : `${at}#${occurrence}` };
  });
  const totalPages = data?.totalPages ?? 0;
  const totalCount = data?.totalCount ?? 0;
  const pageSize = data?.requestedPageSize ?? LOGIN_HISTORY_PAGE_SIZE;
  /*
   * `getCurrent()`, not `getLocale()`. `getLocale()` re-derives the locale from the URL's `?lang=`
   * param or `navigator.language` and **ignores `Locale.change()`** — so it reports the *source* the
   * app started from, not the locale actually in effect. `PagesControlcenterApp` reads `getLocale()`
   * once and commits it with `change()`; every consumer after that must read `getCurrent()`, or a
   * locale switch renders French labels beside English dates.
   */
  const locale = Locale.getCurrent();

  // FR-040: zero pages is the empty state, and it is explicitly **not** an error — the endpoint
  // returns 200 with `totalCount: 0`, never a 404.
  const isEmpty = totalPages === 0 || entries.length === 0;

  const firstEntryOnPage = (page - 1) * pageSize + 1;
  // Clamped, so the final partial page does not claim entries that do not exist.
  const lastEntryOnPage = Math.min(page * pageSize, totalCount);

  const statusMessage = isPending
    ? (Locale.getText("LOGIN_LOADING_ANNOUNCEMENT") as string)
    : isError
      ? (Locale.getText("LOGIN_FAILED_ANNOUNCEMENT") as string)
      : (Locale.getText("LOGIN_LOADED_ANNOUNCEMENT") as string);

  return (
    <TabState
      announcement={statusMessage}
      emptyMessage={Locale.getText("EMPTY_LOGINS") as string}
      isEmpty={isEmpty}
      isError={isError}
      isLoading={isPending}
      loadingActionName={"Loading My Info login history"}
      testId={"login-history"}
    >
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px" }}>
        {/*
         * The header band, per the prototype's `.stable thead th`. A single-column table of
         * near-identical date strings has no other visual landmark, so without a banded header the
         * column label reads as just the first row of data. The heavier bottom rule is what marks
         * where the header stops and the data starts.
         *
         * Styled through `SimpleTable`'s `sx` because `TableCell` and `TableHead` expose only
         * `className` — no `sx` of their own. The selector is `thead th`: plain element names, so
         * unlike a `.Mui*` hook it cannot break on an Interstate class rename.
         */}
        <SimpleTable
          background={"white"}
          data-testid={"login-history-table"}
          sx={{
            "& thead th": {
              backgroundColor: SURFACE_SUBDUED,
              borderBottom: `2px solid ${BORDER_MUTED}`,
              color: ON_SURFACE_MUTED,
              fontSize: "12px",
              fontWeight: 600,
              letterSpacing: "0.4px",
              textTransform: "uppercase"
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>{Locale.getText("LOGGED_IN") as string}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.key}>
                <TableCell data-testid={"login-entry"}>
                  {formatLoginDate(row.at, locale)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </SimpleTable>

        {/*
         * Range on the left, controls on the right — the prototype's `.pager-bar`. The two are
         * different kinds of thing: one is a statement about the whole result set, the other is a
         * control cluster. Run together on the left, as they were, the range reads as a label for the
         * buttons beside it and the row has no anchor at either end.
         *
         * `marginLeft: auto` on the controls rather than `justifyContent: space-between`, so that
         * when the row wraps at a narrow width the controls stay grouped and left-aligned instead of
         * being pushed to the far edge on their own line.
         */}
        <Box
          sx={{
            alignItems: "center",
            columnGap: SPACE_BLOCK,
            display: "flex",
            flexWrap: "wrap",
            rowGap: "8px"
          }}
        >
          <Typography data-testid={"login-range"} tag={"p"} variant={"body-sm"}>
            {String(
              Locale.getText("PAGER_RANGE", {
                from: firstEntryOnPage,
                to: lastEntryOnPage,
                total: totalCount
              })
            )}
          </Typography>

          <Box
            sx={{
              alignItems: "center",
              columnGap: "8px",
              display: "flex",
              flexWrap: "wrap",
              marginLeft: "auto"
            }}
          >
            <Typography
              data-testid={"login-page-position"}
              tag={"p"}
              variant={"body-sm"}
            >
              {String(Locale.getText("PAGER_POS", { page, totalPages }))}
            </Typography>

            {/*
             * FR-038 wants first, previous, next, and last. Interstate's `Pagination` supports
             * `showFirstButton` but **never forwards `showLastButton`** to MUI, so "last" cannot come
             * from it at all. Rather than mix an icon-only first button from inside the control with a
             * text last button beside it, both jumps are rendered here as labelled `Button`s — which
             * also reads better than icons alone for FR-050's "announced as such". Previous, next, and
             * the page numbers come from `Pagination`, whose MUI items are already labelled.
             */}
            <Button
              analyticsConfiguration={{
                eventName: "myinfo-login-history-first-page",
                eventProperties: undefined
              }}
              buttonStyle={"tertiary"}
              data-testid={"login-first-page"}
              disabled={page <= 1}
              onClick={() => setPage(1)}
            >
              {Locale.getText("PAGER_FIRST") as string}
            </Button>

            <Pagination
              count={totalPages}
              data-testid={"login-pagination"}
              page={page}
              onChange={setPage}
            />

            <Button
              analyticsConfiguration={{
                eventName: "myinfo-login-history-last-page",
                eventProperties: undefined
              }}
              buttonStyle={"tertiary"}
              data-testid={"login-last-page"}
              disabled={page >= totalPages}
              onClick={() => setPage(totalPages)}
            >
              {Locale.getText("PAGER_LAST") as string}
            </Button>
          </Box>
        </Box>
      </Box>
    </TabState>
  );
};
